import { zodResolver } from "@hookform/resolvers/zod";
import { Box, FormControlLabel } from "@mui/material";
import { extractErrorMessage } from "@platform/api";
import { createCaseMutation } from "@platform/api/golden";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  TextField,
} from "@platform/design-system";
import { useMutation } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import { useGoldenClient } from "../api/GoldenClientProvider";

import type { ReactNode } from "react";

export const ADD_CASE_TEST_ID = "golden-add-case";

// The RFP ID as the portal shows it: one optional "#" and a positive ID of up to 19
// digits, surrounding spaces allowed. The API takes the bare digits.
const RFP_ID_PATTERN = /^[1-9][0-9]{0,18}$/;

const addCaseSchema = z.object({
  sourceRfpId: z
    .string()
    .trim()
    .transform((value) => value.replace(/^#/, ""))
    .pipe(
      z
        .string()
        .regex(
          RFP_ID_PATTERN,
          "Enter the RFP ID shown in the portal, for example 12345",
        ),
    ),
  useFinalDocuments: z.boolean(),
});

type AddCaseInput = z.input<typeof addCaseSchema>;
type AddCaseValues = z.output<typeof addCaseSchema>;

/**
 * Adds a production RFP to the golden set as a new case, then opens the case, which shows its
 * import. The service names the case from its baseline; the case page changes the label.
 */
export const AddCaseForm = ({
  onCreated,
}: {
  readonly onCreated: () => Promise<void>;
}): ReactNode => {
  const client = useGoldenClient();
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm<
    AddCaseInput,
    unknown,
    AddCaseValues
  >({
    resolver: zodResolver(addCaseSchema),
    defaultValues: { sourceRfpId: "", useFinalDocuments: true },
  });
  const createCase = useMutation({
    ...createCaseMutation({ client }),
    onSuccess: async (created) => {
      await onCreated();
      await navigate(`/cases/${created.id}`);
    },
  });

  const onSubmit = handleSubmit(({ sourceRfpId, useFinalDocuments }) =>
    createCase.mutate({
      body: {
        source_rfp_id: sourceRfpId,
        use_final_documents: useFinalDocuments,
      },
    }),
  );

  return (
    <Card data-testid={ADD_CASE_TEST_ID} title="Add a production RFP">
      <Box
        component="form"
        noValidate
        onSubmit={(event) => void onSubmit(event)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {createCase.isError && (
          <Alert data-testid={`${ADD_CASE_TEST_ID}-error`} severity="error">
            {extractErrorMessage(createCase.error, "Failed to create the case")}
          </Alert>
        )}
        {/* The design-system fields forward their ref to the wrapper, not the input,
            so they are controlled rather than registered. */}
        <Controller
          control={control}
          name="sourceRfpId"
          render={({ field: { ref: _ref, ...field }, fieldState }) => (
            <TextField
              data-testid={`${ADD_CASE_TEST_ID}-rfp-id`}
              label="Broker-portal RFP ID"
              placeholder="The number shown in the portal, for example 12345"
              required
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
              error={fieldState.error !== undefined}
              helperText={fieldState.error?.message}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="useFinalDocuments"
          render={({ field: { ref: _ref, value, onChange, ...field } }) => (
            <FormControlLabel
              label="Use final documents: RUMI reads the Final RFP Documents for Carriers in place of the broker's own. Fixed once the case is created."
              control={
                <Checkbox
                  data-testid={`${ADD_CASE_TEST_ID}-use-final`}
                  checked={value}
                  onChange={(event) => onChange(event.target.checked)}
                  {...field}
                />
              }
            />
          )}
        />
        <Box>
          <Button
            type="submit"
            data-testid={`${ADD_CASE_TEST_ID}-submit`}
            loading={createCase.isPending}
          >
            Add case
          </Button>
        </Box>
      </Box>
    </Card>
  );
};
